import { useMemo, useState } from "react";
import { Button } from "@diametral/design-system/react";

import { useReports } from "../../api/reports";
import type { PersonBrief, ProjectDetail } from "../../api/types";
import { ReportsByWeek } from "../../components/Reports";
import { SelectField } from "../../components/SelectField";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../../components/States";
import { useTranslation } from "../../lib/i18n";

const PAGE = 100;
const MAX = 500;

/** Submitted reports of the project (plus my own drafts), by week, filterable by stream/person. */
export function ReportsTab({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const [streamId, setStreamId] = useState("");
  const [personId, setPersonId] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const reports = useReports({
    project: project.slug,
    stream_id: streamId ? Number(streamId) : undefined,
    person_id: personId ? Number(personId) : undefined,
    limit,
  });

  // People to filter on: everyone ever seen in a stream of the project or in a loaded report.
  const people = useMemo(() => {
    const byId = new Map<number, PersonBrief>();
    for (const stream of project.streams) for (const m of stream.members) byId.set(m.id, m);
    for (const r of reports.data ?? []) byId.set(r.person.id, r.person);
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "fr"));
  }, [project.streams, reports.data]);

  const count = reports.data?.length ?? 0;

  return (
    <div className="ada-stack ada-stack--loose">
      <div className="ds-block-filter-bar-01 ada-filters" role="search" aria-label={t("Filter reports")}>
        <SelectField
          label="Stream"
          value={streamId}
          onChange={(v) => {
            setStreamId(v);
            setLimit(PAGE);
          }}
          options={[
            { value: "", label: t("All streams") },
            ...project.streams.map((s) => ({ value: String(s.id), label: s.archived ? t("{{title}} (archived)", { title: s.title }) : s.title })),
          ]}
        />
        <SelectField
          label={t("Person")}
          value={personId}
          onChange={(v) => {
            setPersonId(v);
            setLimit(PAGE);
          }}
          options={[{ value: "", label: t("All people") }, ...people.map((p) => ({ value: String(p.id), label: p.name }))]}
        />
        <div className="ds-block-filter-bar-01__spacer" />
        <span className="ada-faint">{t("Submitted reports, most recent first.")}</span>
      </div>

      {reports.isPending ? (
        <LoadingBlock lines={5} label={t("Loading reports…")} />
      ) : reports.isError ? (
        <ErrorBlock error={reports.error} onRetry={() => void reports.refetch()} />
      ) : count === 0 ? (
        <EmptyBlock
          icon="file"
          title={t("No report")}
          description={streamId || personId ? t("No report submitted for these filters.") : t("Reports submitted on this project will appear here.")}
        />
      ) : (
        <>
          <ReportsByWeek reports={reports.data} />
          {count >= limit && limit < MAX ? (
            <div>
              <Button variant="outline" loading={reports.isFetching} onClick={() => setLimit((l) => Math.min(l + PAGE, MAX))}>
                {t("Show more reports")}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
